/**
 * `GET /v1/payment-options` — which rails can actually carry this (go-wallet FR-16.5).
 *
 * # Why this replaced a hand-written list
 *
 * The Send panel's destination picker was a table in this repo: four rails, four
 * sentences I wrote about how long each takes. Three of the four are not priced
 * on the customer's schedule and one of those is not routable at all — so three
 * of four choices led to "your fee list does not price this", and the arrival
 * times were guesses.
 *
 * The contract is explicit about why a client must not do that:
 *
 *   "The answer comes from the ROUTING ENGINE — the same plan, from the same
 *    planner, that the authorisation seam resolves when the payment is actually
 *    instructed. That is the whole reason this route exists rather than a client
 *    filtering a catalog: two sources of truth about availability disagree on
 *    exactly the days it matters."
 *
 * So availability, the refusal sentence and the arrival date are all the
 * backend's. What stays here is the HUMAN NAME for a rail — `sepa_inst` is not
 * a thing to show somebody — and nothing else.
 *
 * # A withheld method is a refusal with a reason attached
 *
 * "A method withheld here is one POST /v1/cash-outs would refuse." An
 * unavailable option is rendered, disabled, with the backend's own sentence,
 * rather than hidden: a choice that silently disappears is one somebody looks
 * for and cannot find, and "why can I not send to X" has an answer worth showing.
 */
import { customerMessage } from '@/lib/api'
import type { WalletApi } from '@/lib/api'

export interface PaymentOption {
  /** The catalog code — `sepa`, `sepa_inst`, `swift`, `internal`, … */
  readonly method: string
  /** True only when the routing engine could carry this, now, at this amount. */
  readonly available: boolean
  /** The rail this instruction would take. At most one carries it. */
  readonly selected: boolean
  /** The backend's sentence for why not. Present when unavailable. */
  readonly message?: string
  /** When the money would land. An ESTIMATE, and only on an available option. */
  readonly valueDate?: string
}

export type PaymentOptionsState =
  | { readonly status: 'idle' }
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly options: readonly PaymentOption[] }
  /** We could not ask. NEVER rendered as "nothing is available". */
  | { readonly status: 'unavailable'; readonly reason: string }

interface OptionDTO {
  method?: unknown
  available?: unknown
  selected?: unknown
  message?: unknown
  value_date?: unknown
}

function str(v: unknown): string | undefined {
  return typeof v === 'string' && v !== '' ? v : undefined
}

export async function fetchPaymentOptions(
  api: WalletApi,
  currency: string,
  amountMinor: bigint | null,
): Promise<PaymentOptionsState> {
  // THE AMOUNT IS REQUIRED, and the first cut of this treated it as optional.
  //
  // The contract: "The movement, in the currency's own minor units, as a whole
  // number greater than zero", `required: true`. Omitting it is a 400, which
  // this then rendered as "we could not read where you can send" — a failure
  // message for a question nobody had asked yet, shown to everybody the moment
  // the screen loaded.
  //
  // `idle` and not an error: before an amount there is nothing to ask about.
  // Availability is priced AT an amount — a rail with a ceiling is available
  // below it and not above — so "which rails can carry this" is not a question
  // with an answer until "this" exists.
  if (amountMinor === null || amountMinor <= 0n) return { status: 'idle' }

  const params = new URLSearchParams({ direction: 'out', currency })
  // String(bigint), exact at every magnitude — never Number() (CLAUDE.md §3).
  params.set('amount_minor', String(amountMinor))

  let dto: { options?: unknown }
  try {
    dto = await api.get<{ options?: unknown }>(`/v1/payment-options?${params.toString()}`)
  } catch (err) {
    return { status: 'unavailable', reason: customerMessage(err, 'Please try again in a moment.') }
  }

  const raw = dto?.options
  if (!Array.isArray(raw)) {
    // NOT an empty list. "Nothing can carry this" is a real answer the backend
    // gives with `routed: false` AND a populated list saying why for each; a
    // body with no array at all is a contract this app does not recognise.
    return { status: 'unavailable', reason: 'the rails came back in a shape this app does not know' }
  }

  const out: PaymentOption[] = []
  for (const item of raw) {
    const o = (item ?? {}) as OptionDTO
    const method = str(o.method)
    // A method with no code is not renderable and must not become a blank row
    // somebody can select.
    if (method === undefined) continue
    out.push({
      method,
      available: o.available === true,
      selected: o.selected === true,
      ...(str(o.message) ? { message: str(o.message) as string } : {}),
      ...(str(o.value_date) ? { valueDate: str(o.value_date) as string } : {}),
    })
  }
  return { status: 'ready', options: out }
}

/**
 * The human name for a rail, and the fee schedule's kind for it.
 *
 * THIS IS ALL THAT STAYS CLIENT-SIDE. `sepa_inst` is not a thing to show
 * somebody, and the kind/method pairing is a fact about the fee contract rather
 * than about availability: `internal_transfer` is not method-keyed and
 * `outgoing_payment` is, and either mistake is a 400.
 *
 * A method this build has no name for is rendered under its own code rather than
 * dropped — a rail the backend offers and the bundle has never heard of is a
 * choice somebody should still be able to make.
 */
export interface RailDisplay {
  readonly label: string
  readonly kind: string
  readonly method?: string
}

const RAILS: Readonly<Record<string, RailDisplay>> = {
  internal: { label: 'Another Tesora account', kind: 'internal_transfer' },
  sepa: { label: 'A bank account in Europe', kind: 'outgoing_payment', method: 'sepa' },
  sepa_inst: {
    label: 'A bank account in Europe, instantly',
    kind: 'outgoing_payment',
    method: 'sepa_inst',
  },
  swift: { label: 'A bank account outside Europe', kind: 'outgoing_payment', method: 'swift' },
  ach: { label: 'A bank account in the United States', kind: 'outgoing_payment', method: 'ach' },
  open_banking_pis: {
    label: 'Straight from your bank',
    kind: 'outgoing_payment',
    method: 'open_banking_pis',
  },
  wallet_skrill: { label: 'A Skrill wallet', kind: 'outgoing_payment', method: 'wallet_skrill' },
  wallet_paypal: { label: 'A PayPal wallet', kind: 'outgoing_payment', method: 'wallet_paypal' },
}

export function railDisplay(method: string): RailDisplay {
  return RAILS[method] ?? { label: method, kind: 'outgoing_payment', method }
}
